/* eslint-disable no-underscore-dangle */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, Form, Modal,
} from 'react-bootstrap';
import Error from '../../../Components/Error';
import AccountsService from '../../../Services/AccountsService';

// Stable default form state — used both for create mode and as the
// reset target when the modal re-opens. Keys are camelCase here but
// get mapped to snake_case on submit.
function defaultForm() {
  return {
    name: '',
    kind: 'bank',
    currency: 'EUR',
    provider: '',
    identifier: '',
    isActive: true,
    notes: '',
  };
}

// Hydrate the form from an existing Account (edit mode). null/undefined
// fields collapse to '' so React's controlled inputs don't warn about
// uncontrolled/controlled transitions.
function formFromAccount(account) {
  return {
    name: account?.name || '',
    kind: account?.kind || 'bank',
    currency: account?.currency || 'EUR',
    provider: account?.provider ?? '',
    identifier: account?.identifier ?? '',
    isActive: account?.is_active !== false, // default true if undefined
    notes: account?.notes ?? '',
  };
}

// Diff the form state against the original Account and return a patch
// containing only the fields that actually changed. Empty string for an
// optional field is normalised to null (the explicit "clear" semantic).
function buildPatch(original, form) {
  const patch = {};

  if (form.name !== (original?.name || '')) {
    patch.name = form.name;
  }
  if (form.kind !== (original?.kind || 'bank')) {
    patch.kind = form.kind;
  }
  if (form.currency !== (original?.currency || 'EUR')) {
    patch.currency = form.currency;
  }

  // Optional string fields: '' on the form means "clear", null on the
  // original means "was already clear". Coerce both to the same shape
  // before comparing so we don't emit a no-op patch field.
  const origProvider = original?.provider ?? '';
  if (form.provider !== origProvider) {
    patch.provider = form.provider === '' ? null : form.provider;
  }
  const origIdentifier = original?.identifier ?? '';
  if (form.identifier !== origIdentifier) {
    patch.identifier = form.identifier === '' ? null : form.identifier;
  }
  const origNotes = original?.notes ?? '';
  if (form.notes !== origNotes) {
    patch.notes = form.notes === '' ? null : form.notes;
  }

  const origActive = original?.is_active !== false;
  if (form.isActive !== origActive) {
    patch.is_active = form.isActive;
  }

  return patch;
}

function AccountInputModal({
  show, onHide, account, onSaved,
}) {
  const isEdit = !!account;
  const [form, setForm] = useState(() => (account ? formFromAccount(account) : defaultForm()));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  // Reset / re-seed whenever the modal (re)opens or the target account
  // changes. Without this, switching from "create" to "edit Foo" would
  // keep the previous form values.
  useEffect(() => {
    if (!show) return;
    setForm(account ? formFromAccount(account) : defaultForm());
    setError('');
    setValidationError('');
    setSubmitting(false);
  }, [show, account?._id]);

  const patch = useMemo(
    () => (isEdit ? buildPatch(account, form) : {}),
    [isEdit, account, form],
  );
  const patchIsEmpty = isEdit && Object.keys(patch).length === 0;

  const handleField = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setValidationError('');
    setError('');

    if (!form.name.trim()) {
      setValidationError('Name is required');
      return;
    }
    if (!form.kind) {
      setValidationError('Kind is required');
      return;
    }
    if (!form.currency) {
      setValidationError('Currency is required');
      return;
    }
    if (isEdit && patchIsEmpty) {
      // Defensive — the submit button is also disabled in this state.
      setValidationError('No changes to save');
      return;
    }

    setSubmitting(true);
    try {
      let saved;
      if (isEdit) {
        saved = await AccountsService.update(account._id, patch);
      } else {
        const payload = {
          name: form.name.trim(),
          kind: form.kind,
          currency: form.currency,
          is_active: form.isActive,
        };
        // Only include optional fields when the admin filled them in.
        // Sending empty string for `provider` would create an Account
        // with provider="" which is distinct from null (the backend's
        // "not set" representation).
        if (form.provider) payload.provider = form.provider;
        if (form.identifier) payload.identifier = form.identifier;
        if (form.notes) payload.notes = form.notes;
        saved = await AccountsService.create(payload);
      }
      if (onSaved) onSaved(saved);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (submitting) return;
    if (onHide) onHide();
  };

  return (
    <Modal show={show} onHide={handleCancel} size="md">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? (
            <>
              <i className="fa fa-pen me-2" />
              Edit account
              {account?.name ? ` — ${account.name}` : ''}
            </>
          ) : (
            <>
              <i className="fa fa-plus me-2" />
              Add account
            </>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />
        {validationError && (
          <div className="alert alert-warning py-2 mb-3 small" role="alert">
            {validationError}
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="acc_name">
            <Form.Label>
              Name
              {' '}
              *
            </Form.Label>
            <Form.Control
              type="text"
              value={form.name}
              onChange={handleField('name')}
              placeholder="e.g. K&H Business, Stripe, Cash HUF"
              disabled={submitting}
            />
          </Form.Group>

          <div className="row g-2">
            <Form.Group className="col-md-6 mb-3" controlId="acc_kind">
              <Form.Label>
                Kind
                {' '}
                *
              </Form.Label>
              <Form.Select
                value={form.kind}
                onChange={handleField('kind')}
                disabled={submitting}
              >
                <option value="cash">Cash</option>
                <option value="bank">Bank</option>
                <option value="payment_gateway">Payment gateway</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="col-md-6 mb-3" controlId="acc_currency">
              <Form.Label>
                Currency
                {' '}
                *
              </Form.Label>
              <Form.Select
                value={form.currency}
                onChange={handleField('currency')}
                disabled={submitting}
              >
                <option value="EUR">EUR</option>
                <option value="HUF">HUF</option>
              </Form.Select>
            </Form.Group>
          </div>

          <Form.Group className="mb-3" controlId="acc_provider">
            <Form.Label>Provider</Form.Label>
            <Form.Control
              type="text"
              value={form.provider}
              onChange={handleField('provider')}
              placeholder="stripe / barion / wise / kh / payoneer or leave blank"
              disabled={submitting}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="acc_identifier">
            <Form.Label>Identifier</Form.Label>
            <Form.Control
              type="text"
              value={form.identifier}
              onChange={handleField('identifier')}
              placeholder="IBAN, last 4 digits, or leave blank"
              disabled={submitting}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="acc_is_active">
            <Form.Check
              type="checkbox"
              label="Active"
              checked={form.isActive}
              onChange={handleField('isActive')}
              disabled={submitting}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="acc_notes">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.notes}
              onChange={handleField('notes')}
              disabled={submitting}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={submitting || (isEdit && patchIsEmpty)}
          title={isEdit && patchIsEmpty ? 'No changes to save' : ''}
        >
          {(() => {
            if (submitting) return 'Saving…';
            return isEdit ? 'Save changes' : 'Create account';
          })()}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AccountInputModal;
